import type {
  KnowledgeBaseDocumentSourceTypeCountDTO,
  KnowledgeBaseDocumentStatusCountDTO,
} from '@liks/lac-svc-api/esm/models/index';

const DOCUMENT_STATUS_ORDER = [
  'PENDING',
  'SCANNING',
  'UPLOADING',
  'EXTRACTING',
  'EMBEDDING',
  'READY',
  'FAILED',
] as const;

const DOCUMENT_SOURCE_TYPE_ORDER = [
  'UPLOAD',
  'CRAWL',
  'ARCHIVE_EXTRACT',
] as const;

const DOCUMENT_STATUS_ORDER_INDEX = new Map<string, number>(
  DOCUMENT_STATUS_ORDER.map((status, index) => [status, index]),
);

const DOCUMENT_SOURCE_TYPE_ORDER_INDEX = new Map<string, number>(
  DOCUMENT_SOURCE_TYPE_ORDER.map((sourceType, index) => [sourceType, index]),
);

export function sortDocumentStatusCounts(
  items: KnowledgeBaseDocumentStatusCountDTO[],
): KnowledgeBaseDocumentStatusCountDTO[] {
  return [...items].sort((left, right) => {
    const leftStatus = left.status?.trim().toUpperCase() ?? '';
    const rightStatus = right.status?.trim().toUpperCase() ?? '';
    const leftOrder = DOCUMENT_STATUS_ORDER_INDEX.get(leftStatus);
    const rightOrder = DOCUMENT_STATUS_ORDER_INDEX.get(rightStatus);

    if (leftOrder != null && rightOrder != null) {
      return leftOrder - rightOrder;
    }
    if (leftOrder != null) {
      return -1;
    }
    if (rightOrder != null) {
      return 1;
    }
    return leftStatus.localeCompare(rightStatus, undefined, {
      sensitivity: 'base',
    });
  });
}

export function sortDocumentSourceTypeCounts(
  items: KnowledgeBaseDocumentSourceTypeCountDTO[],
): KnowledgeBaseDocumentSourceTypeCountDTO[] {
  return [...items].sort((left, right) => {
    const leftSource = left.sourceType?.trim().toUpperCase() ?? '';
    const rightSource = right.sourceType?.trim().toUpperCase() ?? '';
    const leftOrder = DOCUMENT_SOURCE_TYPE_ORDER_INDEX.get(leftSource);
    const rightOrder = DOCUMENT_SOURCE_TYPE_ORDER_INDEX.get(rightSource);

    if (leftOrder != null && rightOrder != null) {
      return leftOrder - rightOrder;
    }
    if (leftOrder != null) {
      return -1;
    }
    if (rightOrder != null) {
      return 1;
    }
    return leftSource.localeCompare(rightSource, undefined, {
      sensitivity: 'base',
    });
  });
}

export function formatDocumentStatusLabel(
  status: string | null | undefined,
  translate: (key: string) => string,
): string {
  const normalized = status?.trim().toUpperCase() ?? '';
  if (normalized === '') {
    return translate('lac.documents.statusUnknown');
  }

  const key = `knowledge.documentStatus.${normalized}`;
  const translated = translate(key);
  return translated === key ? normalized : translated;
}

export function formatDocumentSourceTypeLabel(
  sourceType: string | null | undefined,
  translate: (key: string) => string,
): string {
  const normalized = sourceType?.trim().toUpperCase() ?? '';
  if (normalized === '') {
    return translate('lac.documents.sourceUnknown');
  }

  const key = `knowledge.sourceType.${normalized}`;
  const translated = translate(key);
  return translated === key ? normalized : translated;
}

export function tenantAnalyticsDisplayName(row: {
  tenantName?: string | null;
  tenantDisplayName?: string | null;
}): string {
  return row.tenantDisplayName?.trim() || row.tenantName?.trim() || '';
}
